/**
 * [카메라 모니터링 페이지]
 * 목적: 세차기 카메라 JPEG 스냅샷을 1초마다 갱신해서 그리드로 표시
 *
 * 운영 LPR(외주)이 별도로 동작하므로 이 페이지에서는 인식 기능을 갖지 않음.
 * 우리 LPR(yolo) 인식 정확도를 검증하려면 /lpr-test 페이지를 사용.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import api from '../api/client';
import { getAdminRole, getAdminStoreCode } from '../utils/auth';

const ALL_CAMERAS = [
  { id: 1, title: '세차기1', subtitle: '세차기1-1', port: 90,  storeCode: 'jeonju' },
  { id: 2, title: '세차기1', subtitle: '세차기1-2', port: 100, storeCode: 'jeonju' },
  { id: 3, title: '세차기2', subtitle: '세차기2-1', port: 120, storeCode: 'jeonju' },
  { id: 4, title: '세차기2', subtitle: '세차기2-2', port: 130, storeCode: 'jeonju' },
  { id: 5, title: '세차기3', subtitle: '세차기3-1', port: 140, storeCode: 'jeonju' },
  { id: 6, title: '세차기3', subtitle: '세차기3-2', port: 150, storeCode: 'jeonju' },
];

/**
 * [단일 카메라 뷰] — 스냅샷 표시만 담당, 인식 기능 없음
 *
 * 카메라 이미지는 <img src> 가 아니라 api(인증 Bearer 포함) 로 blob 을 받아 objectURL 로 표시한다.
 * 백엔드 /api/v1/camera/snapshot 이 JWT+매장스코프로 보호되므로 인증 없는 img 요청은 쓸 수 없다.
 */
function CameraCard({ camera, timestamp }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [error, setError] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api
      .get('/camera/snapshot', { params: { port: camera.port }, responseType: 'blob' })
      .then((res) => {
        if (!alive) return;
        const next = URL.createObjectURL(res.data);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current); // 이전 프레임 objectURL 정리
        urlRef.current = next;
        setImgUrl(next);
        setError(false);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [camera.port, timestamp]);

  // 언마운트 시 마지막 objectURL 정리(메모리 누수 방지)
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  return (
    <Paper
      elevation={1}
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ backgroundColor: '#111', position: 'relative', flex: 1, minHeight: 0 }}>
        {/* img를 항상 mount해서 timestamp 갱신마다 자동 재시도 (한 번 실패해도 다음 갱신 때 복구) */}
        <Box
          component="img"
          src={imgUrl || undefined}
          alt={camera.subtitle}
          onError={() => setError(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            visibility: error || !imgUrl ? 'hidden' : 'visible',
          }}
        />
        {error && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,0.4)', gap: 0.5, p: 1.5, textAlign: 'center',
          }}>
            <VideocamOffIcon sx={{ fontSize: 28 }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              스냅샷을 가져올 수 없음
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, lineHeight: 1.4 }}>
              백엔드 CAMERA_HOST 환경변수와<br />현재 네트워크에서 카메라 접근 가능 여부 확인
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 1.5, py: 1, backgroundColor: '#fff', flexShrink: 0 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#111', lineHeight: 1.3 }}>
          {camera.title}
        </Typography>
        <Typography variant="caption" sx={{ color: '#888' }}>
          {camera.subtitle}
        </Typography>
      </Box>
    </Paper>
  );
}

/**
 * [카메라 모니터링 메인 페이지]
 * 1초마다 스냅샷 갱신만 수행. LPR 인식은 /lpr-test 페이지.
 */
export default function CameraPage() {
  const [timestamp, setTimestamp] = useState(Date.now());

  // 권한·지점은 JWT 에서 (localStorage 직접 읽기는 변조 위험)
  const role      = getAdminRole();
  const storeCode = getAdminStoreCode();

  // manager 인데 storeCode 가 없으면 어느 지점 카메라도 보여주면 안 된다.
  // 과거에는 `!storeCode` 폴백으로 ALL_CAMERAS 가 떨어졌고 → 다른 지점 카메라가 노출됐다.
  const managerWithoutStore = role === 'manager' && !storeCode;

  const CAMERAS = role === 'master'
    ? ALL_CAMERAS
    : ALL_CAMERAS.filter((c) => c.storeCode === storeCode);

  // 1초마다 스냅샷 갱신
  useEffect(() => {
    const interval = setInterval(() => setTimestamp(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: { xs: 'auto', md: 'calc(100vh - 88px)' },
      minHeight: { xs: 0, md: 'calc(100vh - 88px)' },
    }}>
      <Box sx={{
        mb: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        flexWrap: 'wrap',
        gap: 1,
      }}>
        <Typography variant="h5" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          카메라 모니터링
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            1초마다 자동 갱신
          </Typography>
          <Chip
            label="LIVE"
            size="small"
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              fontWeight: 700,
              fontSize: 12,
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%':   { opacity: 1 },
                '50%':  { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          />
        </Box>
      </Box>

      {managerWithoutStore ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          담당 지점이 설정되지 않은 계정입니다. 카메라를 보려면 마스터 관리자에게 지점 할당을 요청하세요.
        </Alert>
      ) : CAMERAS.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          현재 계정의 지점({storeCode || '미지정'})에 등록된 카메라가 없습니다.
        </Alert>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gridTemplateRows: { xs: 'auto', md: 'repeat(2, 1fr)' },
          gap: 1.5,
          flex: 1,
          minHeight: 0,
        }}>
          {CAMERAS.map((camera) => (
            <CameraCard
              key={camera.id}
              camera={camera}
              timestamp={timestamp}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
